import { Suspense } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { post } from "../api/client";
import { useAdminQueue, useMe } from "../api/hooks";
import type { Me } from "../api/types";
import { chips } from "../lib/format";
import { Spinner, cx } from "./ui";

/** Requires a signed-in user (and the admin role if `admin`). */
export function RequireAuth({ admin = false }: { admin?: boolean }) {
  const me = useMe();
  const loc = useLocation();
  if (me.isPending) return <Spinner />;
  if (!me.data) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (admin && me.data.user.role !== "admin") return <Navigate to="/" replace />;
  return <Outlet />;
}

export function Layout() {
  const me = useMe();
  return (
    <div className="mx-auto flex min-h-dvh max-w-5xl flex-col">
      {me.data && <Header me={me.data} />}
      <main className="flex-1 px-4 pb-24 pt-4 sm:pb-10">
        <Suspense fallback={<Spinner />}>
          <Outlet />
        </Suspense>
      </main>
      {me.data && <BottomNav me={me.data} />}
    </div>
  );
}

function Header({ me }: { me: Me }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isAdmin = me.user.role === "admin";
  const queue = useAdminQueue(isAdmin);
  const bell = (me.unreadNotifs ?? 0) + (me.votableCount ?? 0);

  async function logout() {
    await post("/auth/logout");
    qc.clear();
    navigate("/login");
  }

  const link = ({ isActive }: { isActive: boolean }) =>
    cx("rounded-lg px-3 py-2 text-sm", isActive ? "bg-surface-2 font-medium text-fg" : "text-muted hover:text-fg");

  return (
    <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-line bg-bg/90 px-4 py-2 backdrop-blur">
      <Link to="/" className="mr-2 flex items-center gap-2 font-semibold">
        <img src="/favicon.svg" alt="" className="size-6" />
        <span>Markets</span>
      </Link>
      <nav className="hidden gap-1 sm:flex">
        <NavLink to="/" end className={link}>Markets</NavLink>
        <NavLink to="/leaderboard" className={link}>Leaderboard</NavLink>
        {!isAdmin && <NavLink to="/new" className={link}>Create</NavLink>}
        {isAdmin && (
          <NavLink to="/admin" className={link}>
            Admin{queue.data && queue.data.count > 0 && <span className="ml-1.5 rounded-full bg-warn px-1.5 text-xs text-white">{queue.data.count}</span>}
          </NavLink>
        )}
      </nav>
      <div className="ml-auto flex items-center gap-1">
        {!isAdmin && (
          <Link to={`/u/${me.user.username}`} className="rounded-lg bg-surface-2 px-3 py-1.5 text-sm font-medium" title="Your balance">
            {chips(me.user.balanceM)} <span className="text-muted">chips</span>
          </Link>
        )}
        {!isAdmin && (
          <Link to="/notifications" className="relative rounded-lg p-2 text-muted hover:text-fg" aria-label={`Notifications (${bell})`}>
            <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
            </svg>
            {bell > 0 && <span className="absolute right-0.5 top-0.5 min-w-4 rounded-full bg-no px-1 text-center text-[10px] font-semibold text-white">{bell}</span>}
          </Link>
        )}
        <Link to="/settings" className="hidden rounded-lg px-3 py-2 text-sm text-muted hover:text-fg sm:block">{me.user.displayName}</Link>
        <button onClick={logout} className="rounded-lg px-3 py-2 text-sm text-muted hover:text-fg">Log out</button>
      </div>
    </header>
  );
}

/** Thumb-reachable navigation on phones. */
function BottomNav({ me }: { me: Me }) {
  const isAdmin = me.user.role === "admin";
  const item = ({ isActive }: { isActive: boolean }) => cx("flex-1 py-3 text-center text-xs", isActive ? "font-semibold text-accent" : "text-muted");
  return (
    <nav className="fixed inset-x-0 bottom-0 z-10 flex border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] sm:hidden">
      <NavLink to="/" end className={item}>Markets</NavLink>
      <NavLink to="/leaderboard" className={item}>Leaders</NavLink>
      {isAdmin ? <NavLink to="/admin" className={item}>Admin</NavLink> : <NavLink to="/new" className={item}>Create</NavLink>}
      {!isAdmin && <NavLink to={`/u/${me.user.username}`} className={item}>Me</NavLink>}
      <NavLink to="/settings" className={item}>Settings</NavLink>
    </nav>
  );
}
